// 「最近見たページ」をブラウザの localStorage にローカル保持する (スペース横断)。
//
// サーバーには一切保存しない (閲覧履歴はそのブラウザ限定・プライバシー的に無難)。
// Turbo Drive はページ遷移ごとに body を差し替えるため、サイドバーに常駐する
// このコントローラの connect() が毎遷移で発火する → 記録も再描画も自動で走る。
//
// - entry-value に現在ページ情報があれば (= ページ show のとき) 履歴へ push
// - list ターゲットがあれば履歴を描画 (現在ページ自身は除外する)
// - 折りたたみ時は COLLAPSED 件、展開時は EXPANDED 件まで表示
//   (展開状態は保存しない: ページを開くたびに折りたたみから始まる)
import { Controller } from "@hotwired/stimulus"

const KEY = "akebono:recent-pages"
const MAX = 20 // localStorage に保持する最大件数
const COLLAPSED = 5 // 折りたたみ時に見える件数
const EXPANDED = MAX // 展開時に見える件数

export default class extends Controller {
  static targets = ["list", "more", "moreLabel", "moreChevron"]
  static values = { entry: Object } // { id, title, spaceSlug, spaceName, url }

  connect() {
    this.expanded = false // 展開状態は保存しない (毎回 折りたたみから)
    if (this.hasEntryValue && this.entryValue.id) this.record(this.entryValue)
    if (this.hasListTarget) this.render()
  }

  toggleExpand() {
    this.expanded = !this.expanded
    this.render()
  }

  record(entry) {
    const items = this.load().filter((i) => i.id !== entry.id)
    items.unshift({
      id: entry.id,
      title: entry.title,
      spaceName: entry.spaceName,
      url: entry.url,
      at: Date.now(),
    })
    this.save(items.slice(0, MAX))
  }

  render() {
    // 現在ページ自身は履歴から除外して描画する
    const currentId = this.hasEntryValue ? this.entryValue.id : null
    const all = this.load().filter((i) => i.id !== currentId)
    const expanded = this.expanded
    const items = all.slice(0, expanded ? EXPANDED : COLLAPSED)

    this.listTarget.innerHTML = items.length
      ? items.map((i) => this.itemHtml(i)).join("")
      : `<li class="px-2 py-1 text-xs text-akebono-text-subtle">まだありません</li>`

    // リスト下の「もっと見る／閉じる」ボタン。COLLAPSED 件以下なら不要なので隠す。
    if (this.hasMoreTarget) {
      const expandable = all.length > COLLAPSED
      this.moreTarget.classList.toggle("hidden", !expandable)
      this.moreTarget.setAttribute("aria-expanded", expanded ? "true" : "false")
      if (this.hasMoreLabelTarget) {
        this.moreLabelTarget.textContent = expanded ? "閉じる" : `もっと見る (${all.length - COLLAPSED})`
      }
      if (this.hasMoreChevronTarget) this.moreChevronTarget.classList.toggle("rotate-180", expanded)
    }
  }

  itemHtml(i) {
    const space = i.spaceName
      ? `<span class="ml-1 shrink-0 text-[11px] text-akebono-text-subtle">${this.esc(i.spaceName)}</span>`
      : ""
    return `
      <li>
        <a href="${this.esc(i.url)}"
           class="flex items-baseline rounded-md px-2 py-1 text-[13.5px] text-akebono-text hover:bg-akebono-hover transition-colors"
           title="${this.esc(i.title)}">
          <span class="truncate flex-1 min-w-0">${this.esc(i.title)}</span>
          ${space}
        </a>
      </li>`
  }

  load() {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || []
    } catch {
      return []
    }
  }

  save(items) {
    try {
      localStorage.setItem(KEY, JSON.stringify(items))
    } catch {
      // localStorage 不可 (プライベートモード等) は黙って無視
    }
  }

  esc(s) {
    const d = document.createElement("div")
    d.textContent = s ?? ""
    return d.innerHTML
  }
}
